import { type ImgHTMLAttributes, memo, useMemo } from 'react';

import { type WorkspaceAgentPanelImageDimensions } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { getWorkspaceAgentPanelImageSrc } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelImageSrc';

export type WorkspaceAgentPanelImageProps = {
  alt: string;
  className?: string;
  'data-testid'?: string;
  onLoad?: ImgHTMLAttributes<HTMLImageElement>['onLoad'];
  src: string;
} & Partial<WorkspaceAgentPanelImageDimensions>;

/**
 * Every image in the panel goes through here, so the suppression below is made once rather than at
 * each call site.
 *
 * Sources are clip thumbnails and board covers from our own API. Covers may live on customer storage
 * (BYOS), so the host varies, but it never comes from anything the agent writes.
 *
 * Pass a `{ width, height, fit }` object from `workspaceAgentPanelImageDimensions` so imgix requests
 * match the rendered box: `<WorkspaceAgentPanelImage {...WORKSPACE_AGENT_PANEL_PILL_THUMBNAIL} … />`.
 */
export const WorkspaceAgentPanelImage = memo(
  ({ alt, className, 'data-testid': testId, fit, height, onLoad, src, width }: WorkspaceAgentPanelImageProps) => {
    const optimizedSrc = useMemo(
      () => getWorkspaceAgentPanelImageSrc(src, { fit, height, width }),
      [fit, height, src, width],
    );

    return (
      // biome-ignore lint/performance/noImgElement: the host is not known ahead of time, so there is no `remotePatterns` entry for `next/image` to match
      <img alt={alt} className={className} data-testid={testId} onLoad={onLoad} src={optimizedSrc} />
    );
  },
);

WorkspaceAgentPanelImage.displayName = 'WorkspaceAgentPanelImage';
